/**
 * Centralized navigation helper to prevent login redirect issues
 * This utility provides consistent navigation paths based on user role and current location
 */

/**
 * Get the base path for the current user
 * Checks multiple sources in order of reliability:
 * 1. Current URL path (most reliable)
 * 2. User role from context
 * 3. Fallback to student dashboard
 */
export const getBasePath = (location, user) => {
  // First, check the current path - most reliable source
  if (location?.pathname) {
    if (location.pathname.includes('/admin/')) {
      return '/admin/dashboard';
    }
    if (location.pathname.includes('/teachers/')) {
      return '/teachers/dashboard';
    }
    if (location.pathname.includes('/students/')) {
      return '/students/dashboard';
    }
  }

  // Fallback to user role
  if (user?.role) {
    switch (user.role) {
      case 'admin':
        return '/admin/dashboard';
      case 'teacher':
        return '/teachers/dashboard';
      case 'student':
        return '/students/dashboard';
      default:
        return '/students/dashboard';
    }
  }

  // Ultimate fallback
  return '/students/dashboard';
};

/**
 * Get the courses management path
 */
export const getCoursesPath = (location, user) => {
  const basePath = getBasePath(location, user);
  return `${basePath}/courses`;
};

/**
 * Get the course detail path
 */
export const getCourseDetailPath = (courseId, location, user) => {
  const basePath = getBasePath(location, user);
  return `${basePath}/courses/${courseId}`;
};

/**
 * Get the lessons management path for a course
 */
export const getCourseLessonsPath = (courseId, location, user) => {
  const basePath = getBasePath(location, user);
  return `${basePath}/courses/${courseId}/lessons`;
};

/**
 * Get the lesson view path
 */
export const getLessonViewPath = (courseId, lessonId, location, user) => {
  const basePath = getBasePath(location, user);
  return `${basePath}/courses/${courseId}/lessons/${lessonId}/view`;
};

/**
 * Get the lesson edit path
 */
export const getLessonEditPath = (courseId, lessonId, location, user) => {
  const basePath = getBasePath(location, user);
  return `${basePath}/courses/${courseId}/lessons/${lessonId}/edit`;
};

/**
 * Navigate back to the appropriate location
 * Returns -1 for browser back, or a specific path if navigation history is unreliable
 */
export const getBackPath = (location, user, fallbackPath = null) => {
  // If we have a fallback path, use it
  if (fallbackPath) {
    return fallbackPath;
  }

  // Otherwise, determine from current location
  const path = location?.pathname || '';

  // If on a lesson page, go back to lessons list
  if (path.includes('/lessons/') && path.includes('/view')) {
    const courseId = path.match(/courses\/(\d+)/)?.[1];
    if (courseId) {
      return getCourseLessonsPath(courseId, location, user);
    }
  }

  if (path.includes('/lessons/') && path.includes('/edit')) {
    const courseId = path.match(/courses\/(\d+)/)?.[1];
    if (courseId) {
      return getCourseLessonsPath(courseId, location, user);
    }
  }

  // If on lessons list, go back to courses
  if (path.includes('/courses/') && path.includes('/lessons') && !path.includes('/lessons/')) {
    return getCoursesPath(location, user);
  }

  // If on course detail/form, go back to courses list
  if (path.includes('/courses/') && (path.includes('/edit') || path.includes('/new'))) {
    return getCoursesPath(location, user);
  }

  // Default: go to dashboard
  return getBasePath(location, user);
};

/**
 * Safe navigation wrapper
 * Use this instead of navigate(-1) to prevent login redirects
 */
export const navigateBack = (navigate, location, user, fallbackPath = null) => {
  const backPath = getBackPath(location, user, fallbackPath);
  
  // If backPath is a string, navigate to it
  if (typeof backPath === 'string') {
    navigate(backPath);
  } else {
    // Otherwise use browser back
    navigate(-1);
  }
};
